import { Action, createReducer, on } from '@ngrx/store';
import { ApplicationUserDetail, BusinessUnitRef } from '../models/application-user-detail.model';
import { IdNameMap } from '../models/id-name-map.model';
import { TeamMap } from '../models/team-map.model';
import { UserManagementApiActions, UserManagementActions } from './actions';
import { AddEditRacf } from '../models/add-edit-racf.model';
import { CustomerUserAgent } from '../models/customer-user-agent.model';

export interface UserManagementState {
    allBusinessUnits: IdNameMap[];
    allRoles: IdNameMap[];
    allTeams: TeamMap[];
    businessUnits: BusinessUnitRef[];
    inProgress: boolean;
    isFormValid: boolean;
    minRacfUserMapId: number;
    racfs: AddEditRacf[];
    minCustomerUserAgentId: number;
    customerUserAgents: CustomerUserAgent[];
    roleIds: number[];
    savedDetail: ApplicationUserDetail;
    showAllBusinessUnits: boolean;
    showAllRoles: boolean;
    showAllTeams: boolean;
    teamIds: number[];
    userDetail: ApplicationUserDetail;
    error: string;
}

export const initialState: UserManagementState = {
    allBusinessUnits: [],
    allRoles: [],
    allTeams: [],
    businessUnits: [],
    inProgress: false,
    isFormValid: false,
    minRacfUserMapId: 0,
    racfs: [],
    minCustomerUserAgentId: 0,
    customerUserAgents: [],
    roleIds: [],
    savedDetail: null,
    showAllBusinessUnits: false,
    showAllRoles: false,
    showAllTeams: false,
    teamIds: [],
    userDetail: new ApplicationUserDetail(),
    error: ''
};

export const userManagementFeatureKey = 'usermanagement';

const addCurrentPrimaryInList = (id: number, buList: BusinessUnitRef[]): BusinessUnitRef[] => {
    const currIndex = getBuIndex(id, buList);
    const currBu = addPrimary(buList.find(bu => bu.businessUnitId === id));
    return replaceInBuList(currIndex, currBu, buList);

};

const addPrimary = (bu: BusinessUnitRef): BusinessUnitRef => ({
    businessUnitId: bu.businessUnitId,
    isPrimaryBusinessUnit: true
});

const assignBu = (bu: BusinessUnitRef, buList: BusinessUnitRef[]): BusinessUnitRef[] => [...buList, bu];

const createBu = (id: number): BusinessUnitRef => ({
    businessUnitId: id,
    isPrimaryBusinessUnit: false
});

const getBuIndex = (buId: number, buList: BusinessUnitRef[]): number => buList.findIndex(bu => bu.businessUnitId === buId);

const isOnlyBu = (buId: number, buList: BusinessUnitRef[]): boolean => {
    const index = getBuIndex(buId, buList);
    return index >= 0 && buList.length === 1;
};

const includesPrimaryBu = (buList: BusinessUnitRef[]): boolean => {
    const currPrimaryIndex = buList.findIndex(bu => bu.isPrimaryBusinessUnit);
    return currPrimaryIndex >= 0;
};

const isPrimaryBu = (buId: number, buList: BusinessUnitRef[]): boolean => {
    const index = getBuIndex(buId, buList);
    const currPrimaryIndex = buList.findIndex(bu => bu.isPrimaryBusinessUnit);
    return currPrimaryIndex >= 0 && index === currPrimaryIndex;
};

const makeTopBuPrimary = (buList: BusinessUnitRef[]): BusinessUnitRef[] => {
    const topBu = addPrimary(buList.slice(0, 1)[0]);
    const buListTopRemoved = buList.slice(1);
    return [topBu, ...buListTopRemoved];
};

const removeCurrentPrimaryInList = (buList: BusinessUnitRef[]): BusinessUnitRef[] => {
    const currPrimaryBu = removePrimary(buList.find(bu => bu.isPrimaryBusinessUnit));
    const currPrimaryIndex = buList.findIndex(bu => bu.isPrimaryBusinessUnit);
    const buListBeforePrimary = buList.slice(0, currPrimaryIndex);
    const buListAfterPrimary = buList.slice(currPrimaryIndex + 1);
    return [...buListBeforePrimary, currPrimaryBu, ...buListAfterPrimary];
};

const removePrimary = (bu: BusinessUnitRef): BusinessUnitRef => ({
    businessUnitId: bu.businessUnitId,
    isPrimaryBusinessUnit: false
});

const replaceInBuList = (index: number, bu: BusinessUnitRef,  buList: BusinessUnitRef[]): BusinessUnitRef[] => {
    const buListBefore = buList.slice(0, index);
    const buListAfter = buList.slice(index + 1);
    return [...buListBefore, bu, ...buListAfter];
};

const unassignBu = (id: number, buList: BusinessUnitRef[]): BusinessUnitRef[] => buList.filter(bu => bu.businessUnitId !== id);

const userManagementReducer = createReducer<UserManagementState>(
    initialState,
    on(UserManagementApiActions.loadActiveBusinessUnitsSuccess, (state, action): UserManagementState => ({
        ...state,
        allBusinessUnits: action.allBusinessUnits,
        error: ''
    })),
    on(UserManagementApiActions.loadActiveBusinessUnitsFailure, (state, action): UserManagementState => ({
        ...state,
        allBusinessUnits: [],
        error: action.error
    })),
    on(UserManagementApiActions.loadActiveRolesSuccess, (state, action): UserManagementState => ({
        ...state,
        allRoles: action.allRoles,
        error: ''
    })),
    on(UserManagementApiActions.loadActiveRolesFailure, (state, action): UserManagementState => ({
        ...state,
        allRoles: [],
        error: action.error
    })),
    on(UserManagementApiActions.loadActiveTeamsSuccess, (state, action): UserManagementState => ({
        ...state,
        allTeams: action.allTeams,
        error: ''
    })),
    on(UserManagementApiActions.loadActiveTeamsFailure, (state, action): UserManagementState => ({
        ...state,
        allTeams: [],
        error: action.error
    })),
    on(UserManagementApiActions.loadUserDetailSuccess, (state, action): UserManagementState => ({
        ...state,
        userDetail: action.userDetail,
        businessUnits: action.userDetail.businessUnits,
        inProgress: false,
        racfs: action.userDetail.racfs,
        roleIds: action.userDetail.roleIds,
        teamIds: action.userDetail.teamIds,
        customerUserAgents: action.userDetail.customerUserAgents,
        error: ''
    })),
    on(UserManagementApiActions.loadUserDetailFailure, (state, action): UserManagementState => ({
        ...state,
        userDetail: new ApplicationUserDetail(),
        businessUnits: [],
        inProgress: false,
        racfs: [],
        customerUserAgents: [],
        roleIds: [],
        teamIds: [],
        error: action.error
    })),
    on(UserManagementApiActions.saveApplicationUserDetailSuccess, (state, action): UserManagementState => ({
        ...state,
        inProgress: false,
        savedDetail: action.userDetail,
        error: ''
    })),
    on(UserManagementApiActions.saveApplicationUserDetailFailure, (state, action): UserManagementState => ({
        ...state,
        inProgress: false,
        savedDetail: null,
        error: action.error
    })),
    on(UserManagementApiActions.resetUserSecurityStampFailure, (state, action): UserManagementState => ({
        ...state,
        inProgress: false,
        error: action.error
    })),
    on(UserManagementActions.resetUserManagement, (): UserManagementState => ({
        ...initialState
    })),
    on(UserManagementActions.toggleShowAllBusinessUnits, (state): UserManagementState => ({
        ...state,
        showAllBusinessUnits: !state.showAllBusinessUnits
    })),
    on(UserManagementActions.toggleShowAllRoles, (state): UserManagementState => ({
        ...state,
        showAllRoles: !state.showAllRoles
    })),
    on(UserManagementActions.toggleShowAllTeams, (state): UserManagementState => ({
        ...state,
        showAllTeams: !state.showAllTeams
    })),
    on(UserManagementActions.updateUserInfo, (state, action): UserManagementState => {
        const updatedUserDetail = Object.assign(new ApplicationUserDetail(), state.userDetail, action.userDetail);
        return {
            ...state,
            userDetail: updatedUserDetail
        };
    }),
    on(UserManagementActions.toggleSelectedBusinessUnit, (state, action): UserManagementState => {
        const selBuId = action.id;
        const isOnly = isOnlyBu(selBuId, state.businessUnits);
        const isFirst = state.businessUnits.length === 0;
        const isPrimary = isPrimaryBu(selBuId, state.businessUnits);
        let selectedBUs = [...state.businessUnits];

        if (action.selected) {
            const newBu = isFirst ? addPrimary(createBu(selBuId)) : createBu(selBuId);
            selectedBUs = assignBu(newBu, selectedBUs);
        } else {
            if (isOnly) {
                selectedBUs = unassignBu(selBuId, selectedBUs);
            } else {
                if (isPrimary) {
                    selectedBUs = unassignBu(selBuId, selectedBUs);
                    selectedBUs = makeTopBuPrimary(selectedBUs);
                } else {
                    selectedBUs = unassignBu(selBuId, selectedBUs);
                }
            }
        }

        return {
            ...state,
            businessUnits: selectedBUs
        };
    }),
    on(UserManagementActions.togglePrimaryBusinessUnit, (state, action): UserManagementState => {
        const selBuId = action.id;
        const isAssigned = getBuIndex(selBuId, state.businessUnits) >= 0;
        const isPrimary = isPrimaryBu(selBuId, state.businessUnits);
        let selectedBUs = [...state.businessUnits];

        if (isAssigned && !isPrimary) {
            selectedBUs = removeCurrentPrimaryInList(selectedBUs);
            selectedBUs = addCurrentPrimaryInList(selBuId, selectedBUs);
        }

        return {
            ...state,
            businessUnits: selectedBUs
        };
    }),
    on(UserManagementActions.toggleFieldBusinessUnits, (state, action): UserManagementState => {
        let newBusinessUnits = [...state.businessUnits];
        // These business units should added to the fields list (Change Control #97098 & #98561)
        let fieldOffices = [63, 104, 106, 146, 147, 1168];
        // FOOF business unit type (Change control #143727)
        const fieldOfficeOfTheFutureBusinessUnitTypeId = 18;
        const centralArizonaShelterServices = 1238;

        if (action.selected) {
            const toggleFieldBusinessUnits: BusinessUnitRef[] = state.allBusinessUnits
                .filter(bu => (((bu.businessUnitTypeId === fieldOfficeOfTheFutureBusinessUnitTypeId && bu.id !== centralArizonaShelterServices) && (bu.name.toLowerCase().includes('field')) || fieldOffices.indexOf(bu.id) > -1)))
                .map(bu => ({
                    businessUnitId: bu.id,
                    isPrimaryBusinessUnit: false
                }));
            newBusinessUnits = [...state.businessUnits, ...toggleFieldBusinessUnits];
        } else {
            const toggleFieldBuIds: number[] = state.allBusinessUnits
                .filter(bu => (((bu.businessUnitTypeId === fieldOfficeOfTheFutureBusinessUnitTypeId && bu.id !== centralArizonaShelterServices) && (bu.name.toLowerCase().includes('field')) || fieldOffices.indexOf(bu.id) > -1)))
                .map(bu => bu.id);
            newBusinessUnits = newBusinessUnits.filter(newBu => !toggleFieldBuIds.includes(newBu.businessUnitId));
        }

        if (newBusinessUnits.length > 0 && !includesPrimaryBu(newBusinessUnits)) {
            newBusinessUnits =  makeTopBuPrimary(newBusinessUnits);
        }

        return {
            ...state,
            businessUnits: newBusinessUnits
        };
    }),
    on(UserManagementActions.toggleFieldTeams, (state, action): UserManagementState => {
        const selBUs = [...state.businessUnits];
        const newSelectTeamIds = state.allTeams
            .filter(team => selBUs.find(bu => bu.businessUnitId === team.businessUnitId))
            .filter(team => {
                const acctTeams = team.teamDisplayValue.toLowerCase().includes('acct') || team.teamDisplayValue.toLowerCase().includes('account');
                const helpTeams = team.teamDisplayValue.toLowerCase().includes('help');

                switch (action.team) {
                    case 'helpTeams':
                        return helpTeams;
                    case 'acctTeams':
                        return acctTeams;
                    default:
                        return !(helpTeams || acctTeams);
                }
            })
            .map(team => team.teamId);
        let newTeamIds: number[] = [];

        if (action.selected) {
            newTeamIds = [...state.teamIds, ...newSelectTeamIds];
        } else {
            newTeamIds = state.teamIds.filter(teamId => !newSelectTeamIds.includes(teamId));
        }

        return {
            ...state,
            teamIds: newTeamIds
        };
    }),
    on(UserManagementActions.updateSelectedRoles, (state, action): UserManagementState => ({
        ...state,
        roleIds: action.roleIds
    })),
    on(UserManagementActions.updateSelectedTeams, (state, action): UserManagementState => ({
        ...state,
        teamIds: action.teamIds
    })),
    on(UserManagementActions.deleteRacf, (state, action): UserManagementState => {
        const racfToDelete: AddEditRacf = Object.assign(new AddEditRacf(), action.racf);
        racfToDelete.isEdit = false;
        racfToDelete.isDelete = true;
        const racfsToKeep: AddEditRacf[] = state.racfs.filter(racf => racf.racfUserMapId !== racfToDelete.racfUserMapId);
        return {
            ...state,
            racfs: racfToDelete.racfUserMapId >= 0 ? [...racfsToKeep, racfToDelete] : [...racfsToKeep]
        };
    }),
    on(UserManagementActions.addRacf, (state, action): UserManagementState => {
        const newRacf: AddEditRacf = Object.assign(new AddEditRacf(), action.racf);
        const newMinRacfUserMapId = state.minRacfUserMapId - 1;
        newRacf.racfUserMapId = newMinRacfUserMapId;
        return {
            ...state,
            minRacfUserMapId: newMinRacfUserMapId,
            racfs: [...state.racfs, newRacf]
        };
    }),
    on(UserManagementActions.editRacf, (state, action): UserManagementState => {
        const modRacf: AddEditRacf = Object.assign(new AddEditRacf(), action.racf);
        modRacf.isEdit = true;
        const racfsToKeep: AddEditRacf[] = state.racfs.filter(racf => racf.racfUserMapId !== modRacf.racfUserMapId);
        return {
            ...state,
            racfs: [...racfsToKeep, modRacf]
        };
    }),
    on(UserManagementActions.addAssignedCustomer, (state, action): UserManagementState => {
        const newAssignedCustomer: CustomerUserAgent = Object.assign(new CustomerUserAgent(), action.assignedCustomer);
        const newMinCustomerUserAgentId = state.minCustomerUserAgentId - 1;
        newAssignedCustomer.customerUserAgentId = newMinCustomerUserAgentId;
        return {
            ...state,
            minCustomerUserAgentId: newMinCustomerUserAgentId,
            customerUserAgents: [...state.customerUserAgents, newAssignedCustomer]
        };
    }),
    on(UserManagementActions.deleteAssignedCustomer, (state, action): UserManagementState => {
        const assignedCustomerToDelete: CustomerUserAgent = Object.assign(new CustomerUserAgent(), action.assignedCustomer);
        assignedCustomerToDelete.isDelete = true;
        const assignedCustomersToKeep: CustomerUserAgent[] = state.customerUserAgents.filter(customer => customer.customerUserAgentId !== assignedCustomerToDelete.customerUserAgentId);
        return {
            ...state,
            customerUserAgents: assignedCustomerToDelete.customerUserAgentId >= 0 ? [...assignedCustomersToKeep, assignedCustomerToDelete] : [...assignedCustomersToKeep]
        };
    }),
    on(UserManagementActions.setInProgress, (state, action): UserManagementState => ({
        ...state,
        inProgress: action.inProgress
    })),
    on(UserManagementActions.setIsFormValid, (state, action): UserManagementState => ({
        ...state,
        isFormValid: action.isFormValid
    }))
);

export function reducer(state: UserManagementState | undefined, action: Action) {
    return userManagementReducer(state, action);
}
